// 1. React and Core Libraries
import { Client } from "@stomp/stompjs"; // STOMP protocol client for WebSocket
import _, { get, isEmpty } from "lodash"; // Utility functions
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux"; // Redux state management
import { Outlet, useLocation, useNavigate } from "react-router-dom"; // Routing hooks/components
import SockJS from "sockjs-client"; // WebSocket library (fallback support)
import Cookies from "universal-cookie"; // Cookie management

// 2. Static Assets (Images and Icons)
import {
  celcomDigiIcon,
  celcomDigiWordmark,
  user,
} from "../../assets/images/index"; // Image assets

// 3. Redux Slices (State Management)
// Common/global slice
import {
  removeProfilePic,
  resetLoggedUser,
  saveProfilePic,
  setLoggedUser,
  setPageLoading,
  setProjectStatusUpdate,
  setSessionExtend,
  setsessionToken,
} from "../commonSlice";

// Home module slice
import {
  resetHomeData,
  updateSelectedApp,
} from "../../Modules/Home/HomeSlice.js";

// 4. Utilities / Helpers / Constants
import GetProxy from "../../app/GetProxy"; // API proxy setup or service handler
// Light/dark picker at the foot of the rail — hidden while the flag is off.
import { ThemeToggle, THEME_PICKER_ENABLED } from "../theme";
import { routes } from "../routes"; // Route definitions/constants
import { isRouteVisible, roleCanViewRoute } from "../rbac/permissions"; // RBAC helpers
import {
  isRouteBlockedForUser,
  isSetupAdminUser,
} from "../rbac/restrictedUsers"; // Per-username restrictions
import {
  DIRECTORY_GLYPH,
  LICENSE_GLYPH,
  LOGIN_PAGE_GLYPH,
  TOOLS_GLYPH,
} from "./navGlyphs"; // Nav icons a page also shows in its own header
import { DDAllHide, decrypt } from "../utilities/helpers.js"; // General helper functions
import { storageRemove } from "./Storage"; // LocalStorage/sessionStorage utility
import { toast } from "react-toastify"; // Toast popups for live notifications
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  deleteAllNotifications,
  transformNotification,
  buildLiveNotification,
} from "../../Modules/Notifications/notificationService"; // In-app notification API + mappers

// 5. Custom Components
import ProfileUploaderContent from "../../ProfileUploaderContent.jsx"; // Profile picture uploader UI
import WebSocketHandler from "../WebSocketHandler.js"; // WebSocket management handler
import AlertBox from "./Alert.jsx"; // Custom alert box component
import BreadCrumb from "./BreadCrumb"; // Breadcrumb navigation component
import CustomModal from "./CustomModal.jsx"; // Reusable modal component
import HelpTip from "./HelpTip"; // Hover/focus help for an icon or control
import NotificationToast from "../../Modules/Notifications/NotificationToast.jsx"; // Top-right live notification card

// 6. Styles
import "./custom-components.style.scss"; // Custom SCSS styles for components

let client;
const proxy = GetProxy(false);

/* Nav icons, drawn here rather than loaded from `route.icon`.

   The icon files are white-on-transparent images built for the old navy rail —
   an <img> cannot pick up the colour of the row it sits in, so they had to be
   recoloured with a filter, and the Configuration one is a raster that never
   matched the line weight of the others. Drawn inline they inherit
   `currentColor`, so an icon greys out and goes navy with its own label, and
   every one of them carries the same stroke. Keyed by route path; a route
   without an entry still falls back to its image. */
const NAV_ICONS = {
  assetOnboarding: (
    <>
      <rect x="2.5" y="3" width="19" height="7.5" rx="2" />
      <rect x="2.5" y="13.5" width="19" height="7.5" rx="2" />
      <path d="M6.5 6.75h.01M6.5 17.25h.01" />
      <path d="M10.5 6.75h6M10.5 17.25h6" />
    </>
  ),
  scanManagement: (
    <>
      <path d="M3 8V5.5A2.5 2.5 0 0 1 5.5 3H8" />
      <path d="M16 3h2.5A2.5 2.5 0 0 1 21 5.5V8" />
      <path d="M21 16v2.5a2.5 2.5 0 0 1-2.5 2.5H16" />
      <path d="M8 21H5.5A2.5 2.5 0 0 1 3 18.5V16" />
      <circle cx="11.5" cy="11.5" r="3" />
      <path d="M13.8 13.8 16.5 16.5" />
    </>
  ),
  /* A tray with a plus — adding a new asset to the inventory. The upload arrow
     it started as read as "import a file", which is a different action on this
     very page. */
  "assetOnboarding/onboard": (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17" />
      <path d="M12 12.5v4M10 14.5h4" />
    </>
  ),
  "assetOnboarding/approval": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.4 12.2 2.4 2.4 4.8-5.2" />
    </>
  ),
  "configuration/administration/directoryConfig": (
    <>
      <path d="M12 3 4.5 6v5.6c0 4.3 3.1 7.8 7.5 9.4 4.4-1.6 7.5-5.1 7.5-9.4V6L12 3Z" />
      <path d="m9 12 2.2 2.2L15 10.4" />
    </>
  ),
  "configuration/settings/tools": (
    <>
      <path d="M4 6.5h14M4 12h14M4 17.5h14" />
      <circle cx="9" cy="6.5" r="2.1" />
      <circle cx="15" cy="12" r="2.1" />
      <circle cx="8" cy="17.5" r="2.1" />
    </>
  ),
  "configuration/auditLog": (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </>
  ),
  "configuration/usersession": (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8.5 20h7M12 16v4" />
    </>
  ),
  "configuration/administration/userManagement": (
    <>
      <path d="M15.5 19.5v-1.4a3.4 3.4 0 0 0-3.4-3.4H7.4A3.4 3.4 0 0 0 4 18.1v1.4" />
      <circle cx="9.75" cy="8.2" r="3.2" />
      <path d="M20 19.5v-1.4a3.4 3.4 0 0 0-2.6-3.3" />
      <path d="M15.2 5.2a3.2 3.2 0 0 1 0 6" />
    </>
  ),
  "configuration/administration/Schedulers": (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
      <path d="M12 13.5V16l1.8 1.2" />
    </>
  ),
  /* Shared with the tool panels on the Tools page. */
  "configuration/administration/tools": TOOLS_GLYPH,
  /* Shared with the Login Page Configuration content card header. */
  "configuration/administration/loginPageConfiguration": LOGIN_PAGE_GLYPH,
  /* Shared with the Directory Configuration page header. */
  "configuration/settings/directoryConfiguration": DIRECTORY_GLYPH,
  "configuration/settings/smtpConfiguration": (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.8 7 8.2 5.8L20.2 7" />
    </>
  ),
  /* Shared with the License Configuration page header, so the row and the page
     it opens carry the same symbol. */
  "configuration/settings/licenseConfiguration": LICENSE_GLYPH,
  configuration: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
};

const drawNavIcon = (path, className, strokeWidth) => {
  const drawn = NAV_ICONS[path];
  if (!drawn) {
    return null;
  }
  return (
    <span className={className} aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {drawn}
      </svg>
    </span>
  );
};

const renderNavIcon = (route) =>
  drawNavIcon(route.path, "nav-items-icon", 1.6) || (
    <img
      src={route.icon}
      alt={`${route.name} Icon`}
      className="nav-items-icon"
    />
  );

// Sub-items carry the same icon set a step lighter, so a nested page is still
// recognisable at a glance rather than being one of a column of bare labels.
const renderSubNavIcon = (route) =>
  drawNavIcon(route.path, "sub-nav-icon", 1.5);
const Container = () => {
  const cookies = new Cookies();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tabClick, setTabClick] = useState({});
  const [lastTabAccess, setLastTabAccess] = useState(false);
  const [lastFocusByMouse, setLastFocusByMouse] = useState(false);
  const [activeSubItem, setActiveSubItem] = useState("");
  const [expandedSubItems, setExpandedSubItems] = useState({});
  const [activeItem, setActiveItem] = useState("");

  //Profile Pic Uploader State
  const [showUploader, setShowUploader] = useState(false);
  const [profileAlert, setProfileAlert] = useState({
    show: false,
    type: "danger",
    message: "",
  });
  const [alert, setAlert] = useState({
    show: false,
    message: "* File size shouldn’t exceed 150KB.",
    type: "info",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const [hasFileChanged, setHasFileChanged] = useState(false);

  // Notification panel state
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifTab, setNotifTab] = useState("all"); // "all" | "unread"
  const [expandedNotifId, setExpandedNotifId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const notificationRef = useRef(null);
  const sidebarRef = useRef(null);

  /* Set when a stored photo fails to decode, so the monogram stands in for it
     the same way it does when there is no photo at all. */
  const [avatarFailed, setAvatarFailed] = useState(false);

  /* The account row opens a small panel holding the theme switch. Kept in the
     DOM and hidden rather than unmounted, so it can fade in and out. */
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountRef = useRef(null);

  const { userName, userRole, primaryRole, usersProfilePic, displayName } =
    useSelector((state) => state.common.loggedUser);
  const { isSaveProfilePicLoading } = useSelector((state) => state.common);

  const { openAlert, alertType, alertMessage } = useSelector(
    (state) => state.common.commonAlert,
  );

  const pathName =
    get(location, "pathname", "") === "/gnc/ciso-view/Vulnerability" &&
    !userRole.includes("Ciso")
      ? ""
      : get(location, "pathname", "");
  /* Which rows the rail lights up is read off the URL rather than off the last
     click. `activeSubItem` holds one name, so selecting a third-level page
     overwrote the second-level group holding it and the group went dark; and a
     page reached by typing a URL or reloading lit nothing at all.

     A row is lit when the current path is the row's own path or sits under it,
     so the group stays lit while you are anywhere inside it. Compared segment
     by segment — `startsWith` would let /configuration/cmdb-archive match
     /configuration/cmdb. `activeSubItem` still drives which group is open. */
  const pathSegments = pathName.split("/").filter(Boolean);
  const isPathUnder = (routePath) => {
    if (!routePath) {
      return false;
    }
    const target = routePath.split("/").filter(Boolean);
    return target.every((segment, i) => pathSegments[i] === segment);
  };

  const encoded = decrypt(userName);

  /* Setup Admin is a provisioning account: it carries the Admin role in AD only
     so the mapping resolves, and it has no notification inbox of its own. Both
     the role badge on the account card and the bell are therefore hidden for
     it — the same username-level exception as the blocked routes. */
  const isSetupAdmin = isSetupAdminUser(userName);

  /* A newly uploaded photo gets another chance to load. */
  useEffect(() => {
    setAvatarFailed(false);
  }, [usersProfilePic]);

  /* The session endpoint sends `displayName` encrypted, the same as the
     username. Fall back to the raw value so a plain name still shows if the
     backend ever stops encrypting it — `decrypt` answers null on anything it
     cannot read. */
  const decodedDisplayName = (() => {
    const raw = String(displayName || "").trim();
    if (!raw) {
      return "";
    }
    return String(decrypt(raw) || "").trim() || raw;
  })();

  /* The name the account card shows. `displayName` is what the user was given
     in User Management, so it reads the way they expect; the login name is only
     the fallback for accounts that have none. Left as typed — capitalising it
     would lower-case the rest of a two-part name. */
  const accountName = decodedDisplayName || _.capitalize(encoded);

  /* Stands in for the photo when there isn't one. */
  const accountInitial = accountName.charAt(0).toUpperCase();
  const showAccountPhoto = Boolean(usersProfilePic) && !avatarFailed;

  const onNavigate = (e, url) => {
    e.preventDefault();
    dispatch(setPageLoading(true));
    navigate(url);
  };

  const logOut = (e) => {
    e.preventDefault();
    var newURL =
      pathName === "/gnc/ciso-view/Vulnerability"
        ? "/gnc/Vulnerability"
        : pathName;
    sessionStorage.setItem("lastVisitedURL", newURL);
    sessionStorage.setItem("lastVistedURL", newURL);
    sessionStorage.setItem("isLoggedin", false);
    const Apitoken = cookies.get("apitoken");
    const User = cookies.get("username");
    dispatch(setSessionExtend(false));
    fetch(`${proxy}/auth?logout`, {
      method: "GET",
      headers: {
        "DTOP-API-TOKEN": Apitoken,
        USERNAME: User,
      },
      redirect: "manual",
    })
      .then(() => {
        handleLogout();
        dispatch(resetLoggedUser());
        dispatch(resetHomeData());
        dispatch(setsessionToken(false));
        DDAllHide();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleLogout = () => {
    cookies.remove("jwttoken", { path: "/" });
    cookies.remove("Refersh", { path: "/" });
    cookies.remove("apitoken", { path: "/" });
    cookies.remove("username", { path: "/" });
    cookies.remove("referstoken", { path: "/" });
    dispatch(setLoggedUser({ userName: "", userRole: "", token: "" }));
    dispatch(setPageLoading(true));
    dispatch(setsessionToken(false));
    navigate("/auth");
  };

  const handleArrowNavigation = (event) => {
    const submenuOpen = document.querySelector(".nav.small");
    if (submenuOpen) {
      const submenuLinks = submenuOpen.querySelectorAll('[tabIndex="0"]');
      const currentLinkIndex = Array.from(submenuLinks).indexOf(
        document.activeElement,
      );
      if (event.key === "ArrowUp") {
        event.preventDefault();
        const nextLinkIndex =
          currentLinkIndex === 0
            ? submenuLinks.length - 1
            : currentLinkIndex - 1;
        submenuLinks[nextLinkIndex].focus();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        const nextLinkIndex = (currentLinkIndex + 1) % submenuLinks.length;
        submenuLinks[nextLinkIndex].focus();
      }
    }
  };

  const handleDocumentEvent = (event) => {
    if (event.type === "click") {
      setLastFocusByMouse(true);
      setLastTabAccess(false);
    } else if (event.type === "keydown" && event.key === "Tab") {
      setLastFocusByMouse(false);
      setLastTabAccess(true);
    }
  };

  const handleFocusIn = (input, lastFocusByMouse, lastTabAccess) => {
    // Remove classes from all inputs
    document
      .querySelectorAll("input, textarea, button,select")
      .forEach((el) => {
        el.classList.remove("tabClickStyle", "mouseClickStyle");
      });

    // Add class to the current input
    if (lastFocusByMouse) {
      input.classList.add("mouseClickStyle");
    } else if (lastTabAccess) {
      input.classList.add("tabClickStyle");
    }
  };

  const handleFocusOut = (input) => {
    // Remove classes from the input
    input.classList.remove("tabClickStyle", "mouseClickStyle");
  };

  useEffect(() => {
    if (!isEmpty(tabClick)) {
      document.addEventListener("keydown", handleArrowNavigation);
      return () => {
        document.removeEventListener("keydown", handleArrowNavigation);
      };
    }
  }, [tabClick]);

  useEffect(() => {
    document.addEventListener("click", handleDocumentEvent);
    document.addEventListener("keydown", handleDocumentEvent);

    return () => {
      document.removeEventListener("click", handleDocumentEvent);
      document.removeEventListener("keydown", handleDocumentEvent);
    };
  }, []);

  useEffect(() => {
    const focusInHandler = (event) =>
      handleFocusIn(event.target, lastFocusByMouse, lastTabAccess);
    const focusOutHandler = (event) => handleFocusOut(event.target);

    document
      .querySelectorAll("input, textarea, button, select")
      .forEach((input) => {
        input.addEventListener("focusin", focusInHandler);
        input.addEventListener("focusout", focusOutHandler);
        input.addEventListener("mousemove", focusOutHandler);
      });

    return () => {
      document
        .querySelectorAll("input, textarea, button, select")
        .forEach((input) => {
          input.removeEventListener("focusin", focusInHandler);
          input.removeEventListener("focusout", focusOutHandler);
          input.addEventListener("mousemove", focusOutHandler);
        });
    };
  }, [lastFocusByMouse]);

  /// websock
  useEffect(() => {
    client = new Client({
      brokerURL: `${proxy}/websocket`,

      webSocketFactory: () => new SockJS(`${proxy}/websocket`),

      onConnect: () => {
        client.subscribe("/topic/notifications", (message) => {
          const messageData = JSON.parse(message.body); // Parse the JSON data
          cookies.set("ProjectStatusUpdate", messageData, { path: "/" });
          dispatch(setProjectStatusUpdate({ scanStatus: messageData }));
        });

        // Per-user in-app notifications (e.g. asset approval requests). The backend pushes
        // these to /topic/user/{username}; WebSocketHandler owns the logout-flow events on the
        // same topic, so we ignore those here and only ingest inbox notifications.
        if (encoded) {
          client.subscribe(`/topic/user/${encoded}`, (message) => {
            const payload = JSON.parse(message.body);
            if (
              payload?.type === "ROLE_UPDATED" ||
              payload?.type === "USER_DISABLED"
            ) {
              return;
            }
            const item = buildLiveNotification(payload);
            setNotifications((prev) =>
              prev.some((n) => n.id === item.id) ? prev : [item, ...prev],
            );
            // Pop a rich card in the top-right corner, under the bell it belongs to,
            // while the user is online. Clicking it opens the full notification panel.
            // The durable copy already lives in the panel list (above) and in the
            // backend, so this is a transient live cue only.
            toast(
              ({ closeToast }) => (
                <NotificationToast
                  notification={item}
                  onView={() => {
                    setShowNotifications(true);
                    closeToast();
                  }}
                />
              ),
              {
                autoClose: 6000,
                closeButton: true,
                icon: false,
                toastId: `notif-${item.id}`,
              },
            );
          });
        }
      },

      onStompError: (frame) => {
        console.error("WebSocket error:", frame);
      },
    });
    client.activate();

    return () => {
      client.deactivate(); // Cleanup on unmount
    };
  }, [proxy, encoded]);

  // Pulls the list and maps it to the panel's item shape. Shared by the initial
  // load and by the refresh that follows Clear all.
  const loadNotifications = useCallback(async () => {
    const data = await fetchNotifications();
    return Array.isArray(data) ? data.map(transformNotification) : [];
  }, []);

  // Load persisted notifications (incl. ones that arrived while the user was offline) on login.
  useEffect(() => {
    if (!encoded) {
      return;
    }
    let active = true;
    loadNotifications()
      .then((items) => {
        if (active) {
          setNotifications(items);
        }
      })
      .catch((err) => console.error("Failed to load notifications", err));
    return () => {
      active = false;
    };
  }, [encoded, loadNotifications]);

  // Close offcanvas on Escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && showNotifications) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [showNotifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filteredNotifications = notifications.filter((n) =>
    notifTab === "unread" ? !n.read : true,
  );

  const todayNotifs = filteredNotifications.filter((n) => n.group === "today");
  const earlierNotifs = filteredNotifications.filter(
    (n) => n.group === "earlier",
  );

  const markAsRead = (id) => {
    const target = notifications.find((n) => n.id === id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    // Only hit the backend for a genuine unread -> read transition (this also fires on expand).
    if (target && !target.read) {
      markNotificationRead(id).catch((err) =>
        console.error("Failed to mark notification read", err),
      );
    }
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    markAllNotificationsRead().catch((err) =>
      console.error("Failed to mark all notifications read", err),
    );
  };

  // Clear all is backend-driven: wipe the user's notifications, then re-read the
  // list so the panel shows whatever actually survived on the server.
  const clearAllNotifications = () => {
    deleteAllNotifications()
      .then(() => loadNotifications())
      .then(setNotifications)
      .catch((err) => console.error("Failed to clear all notifications", err));
  };

  // Dismiss deletes the notification on the backend. The item is dropped from
  // the list straight away so the panel stays responsive, and a failed delete
  // re-reads the list to put it back.
  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    deleteNotification(id).catch((err) => {
      console.error("Failed to delete notification", err);
      loadNotifications()
        .then(setNotifications)
        .catch(() => {});
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success":
        return (
          <div className="np-icon-wrap np-icon-success">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle
                cx="10"
                cy="10"
                r="9"
                stroke="#12B76A"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M6.5 10.5L8.5 12.5L13.5 7"
                stroke="#12B76A"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      case "warning":
        return (
          <div className="np-icon-wrap np-icon-warning">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 2L18.66 17H1.34L10 2Z"
                stroke="#F79009"
                strokeWidth="1.5"
                fill="none"
                strokeLinejoin="round"
              />
              <path
                d="M10 8V11.5"
                stroke="#F79009"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <circle cx="10" cy="14" r="0.8" fill="#F79009" />
            </svg>
          </div>
        );
      case "error":
        return (
          <div className="np-icon-wrap np-icon-error">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle
                cx="10"
                cy="10"
                r="9"
                stroke="#F04438"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M7.5 7.5L12.5 12.5M12.5 7.5L7.5 12.5"
                stroke="#F04438"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className="np-icon-wrap np-icon-info">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle
                cx="10"
                cy="10"
                r="9"
                stroke="#3B82F6"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M10 6.5V11"
                stroke="#3B82F6"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <circle cx="10" cy="13.5" r="0.8" fill="#3B82F6" />
            </svg>
          </div>
        );
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "scan":
        return "Scan";
      case "approval":
        return "Approval";
      case "system":
        return "System";
      default:
        return "General";
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "critical":
        return "np-priority-critical";
      case "high":
        return "np-priority-high";
      case "medium":
        return "np-priority-medium";
      default:
        return "np-priority-low";
    }
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (message.length <= maxLength) {
      return { text: message, isTruncated: false };
    }
    return {
      text: message.slice(0, maxLength).trimEnd() + "...",
      isTruncated: true,
    };
  };

  const toggleExpandNotification = (id) => {
    setExpandedNotifId((prev) => (prev === id ? null : id));
  };

  const renderNotificationItem = (n, index) => {
    const isExpanded = expandedNotifId === n.id;
    const { text: truncatedText, isTruncated } = truncateMessage(n.message);

    return (
      <div
        key={n.id}
        className={`np-item ${!n.read ? "np-unread" : ""} ${!n.read ? getPriorityClass(n.priority) : ""} ${isExpanded ? "np-item-expanded" : ""}`}
        onClick={() => {
          markAsRead(n.id);
          toggleExpandNotification(n.id);
        }}
        style={{ animationDelay: `${index * 0.04}s` }}
      >
        <div className="np-item-left">{getNotificationIcon(n.type)}</div>
        <div className="np-item-content">
          <div className="np-item-top">
            <div className="np-item-title-row">
              {!n.read && <span className="np-unread-indicator" />}
              <span className="np-item-title">{n.title}</span>
            </div>
            <div className="np-item-meta">
              <span className={`np-item-category np-cat-${n.category}`}>
                {getCategoryLabel(n.category)}
              </span>
              <span className="np-item-time">{n.time}</span>
            </div>
          </div>

          {/* Collapsed: truncated message */}
          <p
            className={`np-item-message ${isExpanded ? "np-item-message-expanded" : ""}`}
          >
            {isExpanded ? n.message : truncatedText}
            {isTruncated && !isExpanded && (
              <span
                className="np-read-more"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpandNotification(n.id);
                }}
              >
                Read more
              </span>
            )}
            {isExpanded && isTruncated && (
              <span
                className="np-read-less"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpandNotification(n.id);
                }}
              >
                Show less
              </span>
            )}
          </p>

          {/* Progress bar only visible when expanded or always for in-progress */}
          {n.progress !== undefined && (
            <div className="np-item-progress">
              <div className="np-progress-bar">
                <div
                  className="np-progress-fill"
                  style={{ width: `${n.progress}%` }}
                />
              </div>
              <span className="np-progress-label">{n.progress}%</span>
            </div>
          )}

          {/* Expanded: Dismiss is the only action offered here */}
          {isExpanded && (
            <div className="np-item-expanded-actions">
              <button
                className="np-item-action-btn np-item-action-delete"
                aria-label="Dismiss notification"
                onClick={(e) => {
                  e.stopPropagation();
                  removeNotification(n.id);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
                Dismiss
              </button>
            </div>
          )}

          {/* Hover actions when collapsed */}
          {!isExpanded && (
            <div className="np-item-actions">
              <button
                className="np-item-action-btn np-item-action-delete"
                aria-label="Dismiss notification"
                onClick={(e) => {
                  e.stopPropagation();
                  removeNotification(n.id);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const [pagetoggle, setpagetoggle] = useState(false);

  const toggleSubSub = (label) => {
    setExpandedSubItems((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const handleRouteClick = (e, route) => {
    setActiveItem(route.name);
    if (!route.childNavs?.length) {
      onNavigate(e, route.path);
    }
    if (route.childNavs?.length) {
      if (activeItem === route.name) {
        setActiveItem(null);
      }
    }
  };
  const handleSubItemClick = (e, sub, route) => {
    e.preventDefault();
    setActiveSubItem(sub.name);
    setActiveItem(route.name);
    if (!sub.navigationDisable) {
      onNavigate(e, sub?.path);
      storageRemove();
      dispatch(updateSelectedApp({}));
    }
  };
  const handleChildNavClick = (e, route, child) => {
    e.preventDefault();
    setActiveItem(route.name);
    setActiveSubItem(child.name);

    if (child.navigationDisable && child.childNavs?.length > 0) {
      toggleSubSub(child.name);
    } else if (!child.navigationDisable) {
      onNavigate(e, child.path);
      storageRemove();
      dispatch(updateSelectedApp({}));
    }
  };
  const handleSubNavClick = (e, route, sub) => {
    setActiveSubItem(sub.name);
    setActiveItem(route.name);

    if (!sub.navigationDisable) {
      onNavigate(e, sub.path);
      storageRemove();
      dispatch(updateSelectedApp({}));
    }
  };
  // Sidebar expands when clicking anywhere inside it (header onClick) and
  // collapses when clicking anywhere outside it.
  useEffect(() => {
    if (!pagetoggle) {
      return;
    }
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setpagetoggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [pagetoggle]);

  // The theme panel closes on a click anywhere outside it, and on Escape —
  // the two ways out of a popup anyone tries without being told.
  useEffect(() => {
    if (!showAccountMenu) {
      return;
    }
    const handlePointerDown = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showAccountMenu]);

  useEffect(() => {
    const segments = pathName.split("/").filter(Boolean);
    if (segments.length === 0) {
      return;
    }

    for (const route of routes) {
      if (
        !route.isNotSideBarNavigation &&
        !route.navigationDisable &&
        route.path === segments[0]
      ) {
        setActiveItem(route.name);

        for (const child of route.childNavs || []) {
          if (!child.isNotSideBarNavigation) {
            if (child.path === `${segments[0]}/${segments[1]}`) {
              setActiveSubItem(child.name);
              if (child.childNavs?.length) {
                setExpandedSubItems((prev) => ({
                  ...prev,
                  [child.name]: true,
                }));
              }
            }

            for (const sub of child.childNavs || []) {
              if (sub.path === `${segments[0]}/${segments[1]}/${segments[2]}`) {
                setActiveSubItem(sub.name);
                setExpandedSubItems((prev) => ({
                  ...prev,
                  [child.name]: true,
                }));
              }
            }
          }
        }

        break;
      }
    }
  }, [pathName, routes]);

  const handleFileChange = (file) => {
    if (!file.type.startsWith("image/")) {
      return setAlert({
        show: true,
        message: "Only image files are allowed.",
        type: "danger",
      });
    }
    if (file.size > 150 * 1024) {
      return setAlert({
        show: true,
        message: "* File size shouldn’t exceed 150KB.",
        type: "danger",
      });
    }

    setSelectedFile(file);
    setAlert({ show: false, message: "", type: "" });
    setHasFileChanged(true);
    setIsImageRemoved(false);
  };

  function cropToCircle(imageBase64, size = 200) {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");

        // Draw a circular clip path
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.clip();

        // Draw the image within the clip
        ctx.drawImage(image, 0, 0, size, size);

        // Export as PNG with transparent corners
        resolve(canvas.toDataURL("image/png"));
      };
      image.src = imageBase64;
    });
  }

  const handleProfilePictureSave = (file) => {
    const decryptedUserName = decrypt(userName);

    dispatch(
      saveProfilePic({
        file: file,
        payload: { userName: decryptedUserName },
      }),
    )
      .unwrap()
      .then((res) => {
        setProfileAlert({
          show: true,
          type: res?.statusCodeValue == 200 ? "success" : "error",
          message: res.body || "something went wrong",
        });
        setShowUploader(false); // Close modal

        // Auto-dismiss alert after 3 seconds
        setTimeout(() => {
          setProfileAlert((prev) => ({ ...prev, show: false }));
        }, 3000);
      })
      .catch((err) => {
        setProfileAlert({
          show: true,
          type: err?.statusCodeValue == 200 ? "success" : "error",
          message: err?.body || "something went wrong",
        });

        setTimeout(() => {
          setProfileAlert((prev) => ({ ...prev, show: false }));
        }, 3000);
      });
  };

  const handleSave = (croppedImageBase64) => {
    const decryptedUserName = decrypt(userName);

    // Case 1: Remove profile pic
    if (isImageRemoved && !selectedFile) {
      dispatch(
        removeProfilePic({
          payload: { userName: decryptedUserName },
        }),
      )
        .unwrap()
        .then((res) => {
          setProfileAlert({
            show: true,
            type: res?.statusCodeValue === 200 ? "success" : "error",
            message: res?.body || "Something went wrong",
          });

          if (res?.statusCodeValue === 200) {
            setShowUploader(false);
          }

          setTimeout(() => {
            setProfileAlert((prev) => ({ ...prev, show: false }));
          }, 3000);
        })
        .catch((err) => {
          setProfileAlert({
            show: true,
            type: "error",
            message: err?.body || "Failed to remove profile picture.",
          });

          setTimeout(() => {
            setProfileAlert((prev) => ({ ...prev, show: false }));
          }, 3000);
        });

      return;
    }

    // Case 2: User selected and cropped a new file
    if (croppedImageBase64) {
      cropToCircle(croppedImageBase64).then((circularImageBase64) => {
        fetch(circularImageBase64)
          .then((res) => res.blob())
          .then((blob) => {
            const file = new File([blob], "profile.png", { type: "image/png" });
            handleProfilePictureSave(file);
          });
      });

      return;
    }

    // Case 3: No action
    setAlert({
      show: true,
      message: "Please select an image or click remove.",
      type: "info",
    });
  };

  const getPreviewImage = () => {
    // 1. If user selected a new file, show its preview
    if (selectedFile) {
      return URL.createObjectURL(selectedFile);
    }

    // 2. If user removed the image, show default
    if (isImageRemoved) {
      return user; // default image import
    }

    // 3. If user has existing profile pic, show it
    if (usersProfilePic) {
      return `data:image/jpeg;base64,${usersProfilePic}`;
    }

    // 4. Fallback to default image
    return user;
  };

  const onRemove = () => {
    setIsImageRemoved(true);
    setSelectedFile(null); // Clear any selected file
    setHasFileChanged(true);
    setAlert({ show: false, message: "", type: "" }); // Clear any previous alerts
  };

  /* The account's role, as one name. It comes off the auth response already
     chosen and already spelt for display — the backend picks the highest role
     the account holds out of Roles.HIERARCHY and falls back to "User".

     This replaces a formatter that took userRole[0] and rewrote a handful of
     spellings on the way out. Taking [0] was arbitrary once an account held
     more than one role, and the rewrite list had gone stale: it mapped names
     from the older product that no role here carries any more, and its
     Division Head branch compared against a string with a stray quote in it,
     so that one never matched anyway. */
  const roleLabel = () => primaryRole || null;

  return (
    <div className="wrapper_jr">
      <header
        ref={sidebarRef}
        className={`sidebar-header ${pagetoggle ? "expanded" : "collapsed"}`}
        onClick={() => setpagetoggle(true)}
        onMouseLeave={() => setTabClick({})}
      >
        <div className="headerwrap">
          <div className="userwrap">
            {/* The mark is branding only — it used to jump back to the
                landing page, which read as an accidental navigation. */}
            <div className="header-logo">
              <img
                src={celcomDigiIcon}
                alt="CelcomDigi"
                className="brand-icon"
              />
            </div>
            {pagetoggle && (
              <div className="search-container">
                <div className="headexora">
                  <img
                    src={celcomDigiWordmark}
                    alt="CelcomDigi"
                    className="brand-wordmark"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="nav small">
            <ul className="w-100">
              {routes
                .filter(
                  (route) =>
                    !route.isNotSideBarNavigation &&
                    route.navigationDisable !== true &&
                    // RBAC: hide nav items the role can't view, plus any this
                    // specific account is restricted from.
                    isRouteVisible(userRole, route) &&
                    !isRouteBlockedForUser(userName, route),
                )
                .map((route, index) => (
                  <li
                    href={route.iconNavigation ? `/${route.path}` : undefined}
                    className={`nav-items ${
                      pathName.includes(route?.path?.split("/")[0]) && "active"
                    } ${tabClick.index === index ? "tabInde" : ""} `}
                    style={{ cursor: "pointer" }}
                    key={route.path}
                    onClick={(e) => handleRouteClick(e, route)}
                  >
                    <div
                      className={`nav-items ${
                        activeItem === route.name ? "active" : ""
                      } ${
                        !route.navigationDisable ? "navigable" : "non-navigable"
                      }`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        gap: "8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {(NAV_ICONS[route.path] || route.icon) && (
                        <div
                          className="nav-icon-wrap"
                          onClick={() => {
                            setpagetoggle(true);
                          }}
                        >
                          {renderNavIcon(route)}
                        </div>
                      )}

                      {pagetoggle && (
                        <div
                          style={{
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            msOverflowStyle: "none",
                            scrollbarWidth: "none",
                            maxWidth: "200px",
                          }}
                          className="nav-item-text"
                        >
                          {route.name}
                        </div>
                      )}

                      {/* Says the item opens into sub-items, and which way it is
                          currently pointing. Drawn in CSS. */}
                      {pagetoggle && route.childNavs?.length > 0 && (
                        <span
                          className={`nav-caret ${
                            activeItem === route.name ? "nav-caret-open" : ""
                          }`}
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    {pagetoggle &&
                      activeItem === route.name &&
                      route.childNavs?.length > 0 && (
                        <div
                          className="connected-timeline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {route.childNavs
                            .filter((child) => {
                              if (child.isNotSideBarNavigation) {
                                return false;
                              }

                              if (
                                child.name === "Ciso Insights" &&
                                !userRole.includes("Ciso") &&
                                !userRole.includes("Operation")
                              ) {
                                return false;
                              }

                              // RBAC: hide child nav items the role can't view,
                              // plus any restricted for this account.
                              if (
                                !isRouteVisible(userRole, child) ||
                                isRouteBlockedForUser(userName, child)
                              ) {
                                return false;
                              }

                              return true;
                            })
                            .map((child, idx, arr) => {
                              const isFirst = idx === 0;
                              const isLast = idx === arr.length - 1;
                              return (
                                <React.Fragment key={child.name}>
                                  <div className="timeline-node">
                                    <div className="timeline-connector">
                                      {!isFirst && (
                                        <div className="timeline-line-above"></div>
                                      )}
                                      <div
                                        className={`timeline-dot ${
                                          (pathName.includes(
                                            child?.path?.split("/")[1],
                                          ) &&
                                            "active") ||
                                          activeSubItem === child.name
                                            ? "active"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          setActiveSubItem(child.name);
                                          toggleSubSub(child.name);
                                        }}
                                      ></div>
                                      {!isLast && (
                                        <div className="timeline-line-below"></div>
                                      )}
                                    </div>

                                    <div
                                      className={`timeline-heading ${
                                        isPathUnder(child.path) ||
                                        activeSubItem === child.name
                                          ? "active"
                                          : ""
                                      } ${child.navigationDisable ? "" : ""}`}
                                      onClick={(e) =>
                                        handleChildNavClick(e, route, child)
                                      }
                                      style={{
                                        cursor: "pointer",
                                        flex: 1,
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                      }}
                                    >
                                      {renderSubNavIcon(child)}
                                      {/* Clicking anywhere in the hover area navigates */}
                                      <a
                                        href={
                                          !child.navigationDisable
                                            ? `/${child?.path}`
                                            : undefined
                                        }
                                        style={{
                                          color: "inherit",
                                          textDecoration: "none",
                                          cursor: "pointer",
                                          textAlign: "left",
                                        }}
                                      >
                                        {child.text}
                                      </a>

                                      {/* Same caret the top level carries, for
                                          the sub-items that open further. */}
                                      {child.navigationDisable === true &&
                                        child.childNavs?.length > 0 && (
                                          <span
                                            className={`nav-caret ${
                                              expandedSubItems[child.name]
                                                ? "nav-caret-open"
                                                : ""
                                            }`}
                                            aria-hidden="true"
                                          />
                                        )}
                                    </div>

                                    {/* Arrow toggles sub-nav - moved to the end */}
                                  </div>

                                  {/* Sub-child timeline */}
                                  {pagetoggle &&
                                    expandedSubItems[child.name] &&
                                    child.navigationDisable === true &&
                                    child.childNavs?.length > 0 && (
                                      <div className="sub-timeline">
                                        {child.childNavs
                                          .filter(
                                            (sub) =>
                                              !sub.isNotSideBarNavigation &&
                                              // RBAC: hide sub-nav items the
                                              // role can't view, plus any
                                              // restricted for this account.
                                              roleCanViewRoute(userRole, sub) &&
                                              !isRouteBlockedForUser(
                                                userName,
                                                sub,
                                              ),
                                          )
                                          .map((sub, subIdx) => (
                                            <div
                                              key={sub.name}
                                              className="sub-timeline-node"
                                              onClick={(e) =>
                                                handleSubItemClick(
                                                  e,
                                                  sub,
                                                  route,
                                                )
                                              }
                                            >
                                              <div className="sub-timeline-connector">
                                                <div className="sub-timeline-line-above"></div>
                                                <div
                                                  className={`sub-timeline-dot ${
                                                    (pathName.includes(
                                                      sub?.path?.split("/")[2],
                                                    ) &&
                                                      "active") ||
                                                    activeSubItem === sub.name
                                                      ? "active"
                                                      : ""
                                                  }`}
                                                  onClick={(e) =>
                                                    handleSubNavClick(
                                                      e,
                                                      route,
                                                      sub,
                                                    )
                                                  }
                                                ></div>
                                                {subIdx <
                                                  child.childNavs.length -
                                                    1 && (
                                                  <div className="sub-timeline-line-below"></div>
                                                )}
                                              </div>
                                              <a
                                                href={`/${sub.path}`}
                                                onClick={(e) =>
                                                  handleSubItemClick(
                                                    e,
                                                    sub,
                                                    route,
                                                  )
                                                }
                                                className={`sub-timeline-content ${
                                                  isPathUnder(sub.path) ||
                                                  activeSubItem === sub.name
                                                    ? "active"
                                                    : ""
                                                }`}
                                                style={{
                                                  textDecoration: "none",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                {renderSubNavIcon(sub)}
                                                {sub.text}
                                              </a>
                                            </div>
                                          ))}
                                      </div>
                                    )}
                                </React.Fragment>
                              );
                            })}
                        </div>
                      )}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </header>

      <div className={pagetoggle ? "toggle-main" : "mainwrap content"}>
        <div className="topwrap">
          <div className="top_Header">
            <div className="topheader">
              <div className="topheader-left">
                {pathName !== "/access-denied" && (
                  <BreadCrumb path={pathName} />
                )}
              </div>
              <div className="topheader-right">
                <div className="notify-block">
                  {!isSetupAdmin && (
                    <HelpTip
                      placement="bottom"
                      text={
                        showNotifications
                          ? ""
                          : unreadCount > 0
                            ? `Notifications - ${unreadCount} unread. Click to view your alerts and updates.`
                            : "Notifications - click to view your alerts and updates."
                      }
                    >
                      <a
                        className="notify-bell"
                        onClick={() => setShowNotifications(true)}
                        role="button"
                        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 22"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16 7A6 6 0 1 0 4 7c0 7-3 9-3 9h18s-3-2-3-9ZM11.73 20a2 2 0 0 1-3.46 0"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {unreadCount > 0 && (
                          <span className="notify-badge">{unreadCount}</span>
                        )}
                      </a>
                    </HelpTip>
                  )}
                </div>

                <div className="topheader-divider" aria-hidden="true" />

                <div
                  className="header-account"
                  ref={accountRef}
                  onClick={(e) => e.stopPropagation()}
                >
                  {THEME_PICKER_ENABLED && (
                    <div
                      className={`account-menu ${
                        showAccountMenu ? "account-menu-show" : ""
                      }`}
                      role="menu"
                      aria-label="Theme"
                    >
                      <div className="account-menu-row">
                        <span className="account-menu-label">Dark theme</span>
                        <ThemeToggle menuItem />
                      </div>
                    </div>
                  )}

                  <div
                    className={`account-card ${
                      showAccountMenu ? "account-card-open" : ""
                    }`}
                  >
                    <div className="account-meta">
                      <HelpTip placement="bottom" text={accountName}>
                        <span className="account-name">{accountName}</span>
                      </HelpTip>
                      {encoded !== "sysadmin" && !isSetupAdmin && (
                        <span className="account-role">{roleLabel()}</span>
                      )}
                    </div>

                    <div className="loginico">
                      {showAccountPhoto ? (
                        <img
                          src={`data:image/jpeg;base64,${usersProfilePic}`}
                          alt="User Profile"
                          onError={() => setAvatarFailed(true)}
                        />
                      ) : (
                        <span className="account-initial" aria-hidden="true">
                          {accountInitial}
                        </span>
                      )}
                    </div>

                    {/* Sits at the end of the card, away from the name */}
                    <HelpTip
                      placement="bottom"
                      text="Logout"
                      wrapperClassName="account-logout-tip"
                    >
                      <button
                        type="button"
                        className="account-logout"
                        aria-label="Logout"
                        onClick={logOut}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                      </button>
                    </HelpTip>

                    {/* Opens the display panel if enabled */}
                    {THEME_PICKER_ENABLED && (
                      <button
                        type="button"
                        className={`account-caret ${
                          showAccountMenu ? "account-caret-open" : ""
                        }`}
                        aria-label="Display settings"
                        aria-haspopup="true"
                        aria-expanded={showAccountMenu}
                        onClick={() => setShowAccountMenu((open) => !open)}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="wrapper"
            style={pathName === "/access-denied" ? { padding: 0 } : {}}
          >
            <Outlet />
          </div>
        </div>
      </div>
      <AlertBox alert={openAlert} type={alertType} message={alertMessage} />
      {/* Nothing opens this at the moment: the Change Profile entry point was
          taken out of the account row while uploads are failing server-side.
          The uploader and its handlers stay wired, so putting the entry point
          back is all that is needed to restore it. */}
      <CustomModal
        modalOpen={showUploader}
        modalHide={() => setShowUploader(false)}
        closeBtn={true}
        closeBtnFuc={() => {
          setShowUploader(false);
          setAlert({
            show: false,
            message: "* File size shouldn’t exceed 150KB.",
            type: "",
          });
        }}
        modalBody={() => (
          <ProfileUploaderContent
            preview={getPreviewImage()}
            alert={alert}
            onFileChange={handleFileChange}
            onSave={handleSave}
            onRemove={onRemove}
            isLoading={isSaveProfilePicLoading}
            hasFileChanged={hasFileChanged}
            usersProfilePic={usersProfilePic}
            selectedFile={selectedFile}
            isImageRemoved={isImageRemoved}
            // Same stand-in the sidebar uses when there is no photo on file.
            initial={accountInitial}
          />
        )}
        modalCustomClass="profile-upload-modal"
        size="md"
        Modalsize="custom-modal-size"
      />
      <AlertBox
        alert={profileAlert.show}
        type={profileAlert.type}
        message={profileAlert.message}
      />
      <WebSocketHandler onLogout={handleLogout} />

      {/* Notification Offcanvas */}
      <div
        className={`np-offcanvas-backdrop ${showNotifications ? "np-offcanvas-backdrop-show" : ""}`}
        onClick={() => setShowNotifications(false)}
      />
      <div
        className={`np-offcanvas ${showNotifications ? "np-offcanvas-show" : ""}`}
        ref={notificationRef}
        role="dialog"
        aria-label="Notifications"
      >
        {/* Header */}
        <div className="np-header">
          <div className="np-header-top">
            <div className="np-header-left">
              <h4 className="np-title">
                <svg width="22" height="22" viewBox="0 0 22 24" fill="none">
                  <path
                    d="M17 8A6 6 0 1 0 5 8c0 7-3 9-3 9h18s-3-2-3-9ZM12.73 21a2 2 0 0 1-3.46 0"
                    style={{ stroke: "var(--accent-strong, #1a2352)" }}
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Notifications
              </h4>
              {unreadCount > 0 && (
                <span className="np-title-badge">{unreadCount} new</span>
              )}
            </div>
            <button
              className="np-close-btn"
              onClick={() => setShowNotifications(false)}
              aria-label="Close notifications"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M6 6L14 14M14 6L6 14"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className="np-header-toolbar">
            {unreadCount > 0 && (
              <button className="np-action-btn" onClick={markAllAsRead}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M1 7.5L3.5 10L7 6M7 7.5L9.5 10L13 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Mark all read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                className="np-action-btn np-action-danger"
                onClick={clearAllNotifications}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2.5 4H11.5M5.5 4V3C5.5 2.44772 5.94772 2 6.5 2H7.5C8.05228 2 8.5 2.44772 8.5 3V4M3.5 4V11C3.5 11.5523 3.94772 12 4.5 12H9.5C10.0523 12 10.5 11.5523 10.5 11V4"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Clear all
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="np-tabs">
            <button
              className={`np-tab ${notifTab === "all" ? "np-tab-active" : ""}`}
              onClick={() => setNotifTab("all")}
            >
              All
              <span className="np-tab-count">{notifications.length}</span>
            </button>
            <button
              className={`np-tab ${notifTab === "unread" ? "np-tab-active" : ""}`}
              onClick={() => setNotifTab("unread")}
            >
              Unread
              {unreadCount > 0 && (
                <span className="np-tab-count np-tab-count-active">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="np-body">
          {filteredNotifications.length === 0 ? (
            <div className="np-empty">
              <div className="np-empty-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect
                    x="4"
                    y="4"
                    width="40"
                    height="40"
                    rx="20"
                    style={{ fill: "var(--surface-sunken, #F9FAFB)" }}
                  />
                  <rect
                    x="2"
                    y="2"
                    width="44"
                    height="44"
                    rx="22"
                    style={{ stroke: "var(--border-subtle, #EAECF0)" }}
                    strokeWidth="2"
                    strokeDasharray="4 4"
                  />
                  <path
                    d="M30 20a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9ZM25.73 31a2 2 0 0 1-3.46 0"
                    style={{ stroke: "var(--border-strong, #D0D5DD)" }}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="np-empty-title">
                {notifTab === "unread" ? "All caught up!" : "No notifications"}
              </p>
              <p className="np-empty-desc">
                {notifTab === "unread"
                  ? "You've read all your notifications. Check back later."
                  : "When you receive notifications, they'll appear here."}
              </p>
            </div>
          ) : (
            <>
              {todayNotifs.length > 0 && (
                <div className="np-group">
                  <div className="np-group-label">
                    <span>Today</span>
                    <span className="np-group-count">{todayNotifs.length}</span>
                  </div>
                  {todayNotifs.map((n, i) => renderNotificationItem(n, i))}
                </div>
              )}
              {earlierNotifs.length > 0 && (
                <div className="np-group">
                  <div className="np-group-label">
                    <span>Earlier</span>
                    <span className="np-group-count">
                      {earlierNotifs.length}
                    </span>
                  </div>
                  {earlierNotifs.map((n, i) =>
                    renderNotificationItem(n, todayNotifs.length + i),
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="np-footer">
          <span className="np-footer-summary">
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
              : "No unread notifications"}
          </span>
          <button className="np-footer-link">
            View All Notifications
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M5.25 3.5L8.75 7L5.25 10.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Container;
